import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PanResponder, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from './Texte';
import Animated, {
  cancelAnimation,
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { DEPASSEMENT_FAB } from './BottomNav';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { OffreCarte } from './OffreCarte';
import { Heart, X } from './icons';
import { t } from '../i18n';
import { BLOC_LTR, PROPS_LTR } from '../i18n/sens';
import { colors, gradients, radius, shadow, spacing } from '../theme/tokens';
import type { Decision, Offre } from '../types/offres';

export interface PileOffresProps {
  offres: Offre[];
  onDecision: (offre: Offre, decision: Decision) => void;
  onVoirDetail: (offre: Offre) => void;
}

const SEUIL_VITESSE = 0.8;
const DUREE_SORTIE_MS = 220;
const ROTATION_MAX = 12;
// Balancement au repos de la carte du dessus (maquette) : il dit que la carte se saisit. Il s'efface dès
// que le doigt la déplace.
const BALANCEMENT_MS = 2500;
const BALANCEMENT_DEG = 1.5;
const BALANCEMENT_PX = 4;
/**
 * Pile plus basse que ça (iPhone SE : 459 pt mesurés) : boutons réduits pour rendre la hauteur à la
 * carte. Les cibles ne descendent jamais sous 44 pt.
 */
const PILE_BASSE = 500;
const TAILLES = {
  normale: { passer: 60, interesse: 60 },
  basse: { passer: 52, interesse: 52 },
};

/** Ce qu'un geste relâché décide : assez loin, ou assez vif dans son sens ; sinon, retour au centre. */
export function decisionDuGeste(dx: number, vx: number, largeur: number): 'interessee' | 'passee' | null {
  const seuil = Math.min(120, largeur * 0.3);
  if (dx > seuil || (dx > 0 && vx > SEUIL_VITESSE)) return 'interessee';
  if (dx < -seuil || (dx < 0 && vx < -SEUIL_VITESSE)) return 'passee';
  return null;
}

// Sauvegarder n'est plus un geste de la pile : elle se fait depuis le détail de l'offre, et la liste
// des offres sauvegardées s'ouvre par le favori de l'en-tête.
type Geste = Exclude<Decision, 'sauvegardee'>;

const ACTIONS: { name: Geste | 'detail'; label: string }[] = [
  { name: 'interessee', label: 'offres.actions.interesse' },
  { name: 'passee', label: 'offres.actions.passer' },
  { name: 'detail', label: 'offres.carte.voirDetail' },
];

// Montée une fois par carte (clé = offre) : chaque carte repart d'une position et d'un verrou neufs.
function Sommet({
  offre,
  onDecision,
  onVoirDetail,
  basse,
  derriere,
}: { offre: Offre; basse: boolean; derriere: number } & Omit<PileOffresProps, 'offres'>) {
  const taille = TAILLES[basse ? 'basse' : 'normale'];
  const rond = (d: number) => ({ width: d, height: d });
  const { width } = useWindowDimensions();
  const x = useSharedValue(0);
  const balancement = useSharedValue(0);
  const reduit = useReduceMotion();
  const decidee = useRef(false);
  const rappel = useRef(onDecision);
  rappel.current = onDecision;
  const largeur = useRef(width);
  largeur.current = width;

  const terminer = useCallback((d: Decision) => rappel.current(offre, d), [offre]);

  useEffect(() => {
    if (reduit) {
      balancement.value = 0.5;
      return;
    }
    balancement.value = withRepeat(withTiming(1, { duration: BALANCEMENT_MS, easing: Easing.inOut(Easing.sin) }), -1, true);
    return () => cancelAnimation(balancement);
  }, [reduit, balancement]);

  const sortir = useCallback(
    (d: Geste) => {
      if (decidee.current) return;
      decidee.current = true;
      const loin = largeur.current * 1.5;
      const fin = (fini?: boolean) => {
        'worklet';
        if (fini) runOnJS(terminer)(d);
      };
      x.value = withTiming(d === 'interessee' ? loin : -loin, { duration: DUREE_SORTIE_MS }, fin);
    },
    [terminer, x],
  );
  const sortirRef = useRef(sortir);
  sortirRef.current = sortir;

  const geste = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) => !decidee.current && Math.abs(g.dx) > 8 && Math.abs(g.dx) > Math.abs(g.dy),
      // Aussi en phase de capture : un enfant (lien, texte) ne doit jamais garder un glissement horizontal.
      onMoveShouldSetPanResponderCapture: (_, g) =>
        !decidee.current && Math.abs(g.dx) > 8 && Math.abs(g.dx) > Math.abs(g.dy),
      onPanResponderMove: (_, g) => {
        if (!decidee.current) x.value = g.dx;
      },
      onPanResponderRelease: (_, g) => {
        const d = decisionDuGeste(g.dx, g.vx, largeur.current);
        if (d) sortirRef.current(d);
        else x.value = withSpring(0);
      },
      onPanResponderTerminate: () => {
        if (!decidee.current) x.value = withSpring(0);
      },
      onPanResponderTerminationRequest: () => false,
    }),
  ).current;

  const style = useAnimatedStyle(() => {
    // -1 à +1, éteint dès que la carte s'éloigne du centre.
    const bal = (balancement.value * 2 - 1) * Math.max(0, 1 - Math.abs(x.value) / 24);
    return {
      transform: [
        { translateX: x.value + bal * BALANCEMENT_PX },
        { rotate: `${Math.max(-ROTATION_MAX, Math.min(ROTATION_MAX, x.value / 20)) + bal * BALANCEMENT_DEG}deg` },
      ],
    };
  });

  return (
    <>
      {/* La carte et ses bords : ceux-ci dépassent SOUS elle, comme la maquette, et suivent sa hauteur. */}
      <View style={styles.zoneCarte} testID="pile-zone">
        {derriere > 1 ? <View style={styles.apres} testID="pile-apres" /> : null}
        {derriere > 0 ? <View style={styles.suivante} testID="pile-suivante" /> : null}
      <Animated.View
        testID="pile-carte"
        style={[styles.carte, style]}
        accessible
        accessibilityLabel={[offre.titre, offre.entreprise.nom, t('offres.carte.score', { score: offre.score })]
          .filter(Boolean)
          .join(', ')}
        accessibilityHint={t('offres.actions.aide')}
        accessibilityActions={ACTIONS.map((a) => ({ name: a.name, label: t(a.label) }))}
        onAccessibilityAction={(e) => {
          const nom = e.nativeEvent.actionName as Geste | 'detail';
          if (nom === 'detail') onVoirDetail(offre);
          else sortir(nom);
        }}
        {...geste.panHandlers}
      >
        <OffreCarte offre={offre} onVoirDetail={() => onVoirDetail(offre)} testID="offre-carte" />
      </Animated.View>
      </View>

      {/* ✕ À GAUCHE, « M'INTÉRESSE » À DROITE, DANS TOUTES LES LANGUES (décision du 17/10/2026) : le geste
          reste physique, glisser à droite veut dire « intéressé », même en arabe. La rangée ne se
          retourne donc pas avec l'interface, sinon le bouton « intéressé » passerait du côté du « non ». */}
      <View style={[styles.boutons, BLOC_LTR]} {...PROPS_LTR}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('offres.actions.passer')}
          onPress={() => sortir('passee')}
          style={styles.action}
        >
          <View style={[styles.rond, styles.passer, rond(taille.passer)]} testID="pile-rond-passer">
            <X size={24} color={colors.grey500} strokeWidth={2.6} />
          </View>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('offres.actions.interesse')}
          onPress={() => sortir('interessee')}
          style={styles.action}
        >
          {/* L'ombre est portée par une vue au fond plein : Android n'en projette pas depuis un dégradé. */}
          <View style={[styles.interesse, { height: taille.interesse }]} testID="pile-rond-interesse">
            <LinearGradient
              colors={gradients.cta}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={[styles.pilule, { height: taille.interesse }]}
            >
              <Heart size={20} color={colors.white} fill={colors.white} />
              <Text style={styles.piluleTexte}>{t('offres.actions.libelles.interesse')}</Text>
            </LinearGradient>
          </View>
        </Pressable>
      </View>
    </>
  );
}

export function PileOffres({ offres, onDecision, onVoirDetail }: PileOffresProps) {
  const [hauteur, setHauteur] = useState(0);
  const dessus = offres[0];
  if (!dessus) return null;
  const basse = hauteur > 0 && hauteur < PILE_BASSE;
  return (
    <View style={styles.pile} testID="pile" onLayout={(e) => setHauteur(e.nativeEvent.layout.height)}>
      <Sommet
        key={dessus.cle}
        offre={dessus}
        onDecision={onDecision}
        onVoirDetail={onVoirDetail}
        basse={basse}
        derriere={Math.min(2, offres.length - 1)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  // Remplit l'écran : la carte prend tout ce que les boutons laissent. En bas, la place du
  // bouton central de la barre d'onglets, qui dépasse au-dessus d'elle.
  pile: { flex: 1, paddingTop: spacing.md, paddingBottom: DEPASSEMENT_FAB + spacing.xs },
  // Pivot bas, comme une carte tenue par le bas : le balancement et le glissement s'en inspirent.
  carte: { flex: 1, minHeight: 0, transformOrigin: '50% 90%' },
  // Zone de la carte : elle prend la hauteur que les boutons laissent. Les bords des cartes suivantes y sont
  // posés sous la carte et dépassent de 7 et 14 pt en bas, comme la maquette ; la deuxième n'est dessinée
  // que s'il en reste au moins deux derrière la première.
  zoneCarte: { flex: 1, minHeight: 0, marginBottom: 14 },
  suivante: {
    position: 'absolute',
    start: 11,
    end: 11,
    top: 11,
    bottom: -7,
    borderRadius: 28,
    backgroundColor: '#EEF1F7',
  },
  apres: {
    position: 'absolute',
    start: 22,
    end: 22,
    top: 22,
    bottom: -14,
    borderRadius: 28,
    backgroundColor: colors.grey200,
  },
  boutons: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 22,
    marginTop: spacing.sm,
  },
  action: { minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  rond: {
    minWidth: 44,
    minHeight: 44,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  passer: { backgroundColor: colors.white, borderWidth: 1.5, borderColor: colors.bordureClaire, ...shadow.card },
  interesse: { borderRadius: radius.pill, backgroundColor: colors.violetLogo, ...shadow.floating },
  pilule: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingHorizontal: 22,
    borderRadius: radius.pill,
  },
  piluleTexte: { fontSize: 15.5, lineHeight: 20, fontWeight: '800', color: colors.white },
});
